/**
 * Per-item action trigger (A7Y-3870 follow-up; rebuilt on Catalyst
 * `Dropdown`/`Dialog` for A7Y-4407 round 4): a compact "⋯" opens a real
 * menu — Done, Defer…, Waiting on…, That's wrong, and Seen (Seen only ever
 * for a `deployed:` item, per the ticket: "Seen" only makes sense once a
 * shipped ticket has actually been reviewed). Done/That's wrong/Seen fire
 * immediately; Defer…/Waiting on… open a `Dialog` for their date/name input
 * — a real modal, not the round-3 inline sub-form, which is what a
 * `Headless.Menu`'s own portaled items can't host cleanly (round 3's
 * objection to `Dropdown`/`DropdownMenu`: its items render into
 * `document.body`, same as `Dialog`, so moving the two-field forms into a
 * `Dialog` resolves that rather than fighting it). Used on every dense,
 * many-rows section — the card body, Watched, Overdue/Stale, Changes, Call
 * intake, Shipped, Judgment queue — so wiring actions onto every keyed item
 * doesn't turn those sections into a wall of buttons.
 *
 * Once the item already carries a `decision` (either from a prior run's
 * overlay or this page's own optimistic patch), this collapses to a
 * read-only label — acting twice on the same item is a resubmission, not a
 * new decision. A decision inherited from an accepted proposal
 * (`decision.proposal !== null`, D9) is NOT a personal decision — it is
 * code's own bring_forward or `none` effect landing on the target, not
 * Yevhen acting on it (Karen round 1 F2) — it still opens the same menu (in
 * edit mode) plus a "HELM proposal accepted" disclosure note, and acting on
 * it — e.g. Done — replaces it with the actor's own decision on the next
 * patch/fetch.
 *
 * Under `useHelmReadOnly()` (A7Y-3872, D15 — a past run, opened via
 * `?run=<runId>`) the menu itself never renders: a decided item still shows
 * its label and disclosure, an undecided one renders nothing, since a past
 * day is a record of what stood then and no click here can act on it.
 */
import { useState } from 'react';
import type React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
  Field,
  Input,
  Label,
  Select,
} from '@a7y/tailwind-catalyst';
import type { HelmActionInput, HelmDecision } from '@a7y/platform-health/helm';
import { useHelmReadOnly } from './readOnly';
import { useHelmRecordActions } from './recordActions';
import type { CloseDetails } from './recordActions';

/**
 * Done on a roster row (A7Y-5210): the record closes the item with how it was
 * done and what came of it, so Done asks for both before anything is written.
 */
function CloseRosterDialog({
  itemKey,
  open,
  onClose,
  onDone,
}: {
  itemKey: string;
  open: boolean;
  onClose: () => void;
  onDone: (close: CloseDetails) => void;
}) {
  const [route, setRoute] = useState('');
  const [outcome, setOutcome] = useState('');
  const reset = () => {
    setRoute('');
    setOutcome('');
  };
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Done</DialogTitle>
      <DialogBody className="space-y-4">
        <Field>
          <Label>Route</Label>
          <Input
            type="text"
            aria-label={`Route, for ${itemKey}`}
            placeholder="email, call…"
            value={route}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRoute(e.target.value)}
          />
        </Field>
        <Field>
          <Label>Outcome</Label>
          <Input
            type="text"
            aria-label={`Outcome, for ${itemKey}`}
            value={outcome}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOutcome(e.target.value)}
          />
        </Field>
      </DialogBody>
      <DialogActions>
        <Button
          plain
          onClick={() => {
            reset();
            onClose();
          }}
        >
          Cancel
        </Button>
        <Button
          data-testid="helm-action-done-confirm"
          disabled={route.trim() === '' || outcome.trim() === ''}
          onClick={() => {
            onDone({ route: route.trim(), outcome: outcome.trim() });
            reset();
            onClose();
          }}
        >
          Close item
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeferDialog({
  itemKey,
  open,
  onClose,
  onAction,
}: {
  itemKey: string;
  open: boolean;
  onClose: () => void;
  onAction: (input: HelmActionInput) => void;
}) {
  const [deferUntil, setDeferUntil] = useState('');

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Defer</DialogTitle>
      <DialogBody>
        <Field>
          <Label>Defer until</Label>
          <Input
            type="date"
            aria-label={`Defer ${itemKey} until`}
            value={deferUntil}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeferUntil(e.target.value)}
          />
        </Field>
      </DialogBody>
      <DialogActions>
        <Button
          plain
          onClick={() => {
            setDeferUntil('');
            onClose();
          }}
        >
          Cancel
        </Button>
        <Button
          data-testid="helm-action-defer-confirm"
          disabled={!deferUntil}
          onClick={() => {
            onAction({ itemKey, kind: 'defer', deferUntil });
            setDeferUntil('');
            onClose();
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function WaitingOnDialog({
  itemKey,
  open,
  onClose,
  onAction,
  recheckRequired = false,
}: {
  itemKey: string;
  open: boolean;
  onClose: () => void;
  onAction: (input: HelmActionInput) => void;
  /** A7Y-5210: on a roster row the recheck day is the item's new date, so it is required. */
  recheckRequired?: boolean;
}) {
  const [waitingOn, setWaitingOn] = useState('');
  // The side is selectable so an internal wait (e.g. "waiting on Thomas") can be recorded.
  const [waitingSide, setWaitingSide] = useState<'external' | 'internal'>('external');
  const [recheckOn, setRecheckOn] = useState('');

  const reset = () => {
    setWaitingOn('');
    setWaitingSide('external');
    setRecheckOn('');
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Waiting on…</DialogTitle>
      <DialogBody className="space-y-4">
        <Field>
          <Label>Who / what</Label>
          <Input
            type="text"
            aria-label={`Waiting on, for ${itemKey}`}
            placeholder="who / what"
            value={waitingOn}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWaitingOn(e.target.value)}
          />
        </Field>
        <Field>
          <Label>Side</Label>
          <Select
            aria-label={`Waiting-on side, for ${itemKey}`}
            value={waitingSide}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setWaitingSide(e.target.value as 'external' | 'internal')
            }
          >
            <option value="external">them</option>
            <option value="internal">us</option>
          </Select>
        </Field>
        <Field>
          <Label>{recheckRequired ? 'Recheck on' : 'Recheck on (optional)'}</Label>
          <Input
            type="date"
            aria-label={`Recheck ${itemKey} on`}
            value={recheckOn}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRecheckOn(e.target.value)}
          />
        </Field>
      </DialogBody>
      <DialogActions>
        <Button
          plain
          onClick={() => {
            reset();
            onClose();
          }}
        >
          Cancel
        </Button>
        <Button
          data-testid="helm-action-waiting_on-confirm"
          disabled={waitingOn.trim().length === 0 || (recheckRequired && recheckOn === '')}
          onClick={() => {
            onAction({
              itemKey,
              kind: 'waiting_on',
              waitingOn: waitingOn.trim(),
              waitingSide,
              ...(recheckOn ? { recheckOn } : {}),
            });
            reset();
            onClose();
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ItemActionsMenu({
  itemKey,
  decision,
  onAction,
}: {
  itemKey: string;
  decision: HelmDecision | null;
  onAction: (input: HelmActionInput) => void;
}) {
  const readOnly = useHelmReadOnly();
  const record = useHelmRecordActions();
  const [deferOpen, setDeferOpen] = useState(false);
  const [waitingOpen, setWaitingOpen] = useState(false);
  const [doneOpen, setDoneOpen] = useState(false);
  // A7Y-5210: on a roster row, Done, Defer and Waiting on write HELM's record; every other action, and every other item, is the overlay.
  const target = record?.targets.get(itemKey) ?? null;
  const dispatch = (input: HelmActionInput) => {
    if (record && target && (input.kind === 'defer' || input.kind === 'waiting_on')) {
      record.onRecordAction(target, input, null);
    } else onAction(input);
  };

  const isOwnDecision = decision != null && decision.proposal == null;

  if (isOwnDecision) {
    return (
      <span className="text-xs text-zinc-500" data-testid="helm-item-decided">
        {decision.kind}
      </span>
    );
  }

  if (readOnly) {
    // An inherited decision is still disclosed in read-only mode — just
    // never with an action attached to it. An undecided item under a past,
    // read-only run has nothing to act on, so it renders nothing at all.
    if (decision) {
      return (
        <span className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
          <span data-testid="helm-item-decided">{decision.kind}</span>
          <span data-testid="helm-item-proposal-note">HELM proposal accepted</span>
        </span>
      );
    }
    return null;
  }

  const isDeployed = itemKey.startsWith('deployed:');

  return (
    <span className="flex flex-wrap items-center gap-2 text-xs">
      {decision && (
        <span data-testid="helm-item-proposal-note" className="text-zinc-500">
          HELM proposal accepted
        </span>
      )}
      <Dropdown>
        <DropdownButton
          plain
          data-testid="helm-item-menu-toggle"
          aria-label={`Actions for ${itemKey}`}
        >
          ⋯
        </DropdownButton>
        <DropdownMenu anchor="bottom end">
          <DropdownItem
            data-testid="helm-action-done"
            onClick={() => (target ? setDoneOpen(true) : onAction({ itemKey, kind: 'done' }))}
          >
            {target ? 'Done…' : 'Done'}
          </DropdownItem>
          <DropdownItem data-testid="helm-action-defer" onClick={() => setDeferOpen(true)}>
            Defer…
          </DropdownItem>
          <DropdownItem data-testid="helm-action-waiting_on" onClick={() => setWaitingOpen(true)}>
            Waiting on…
          </DropdownItem>
          <DropdownItem
            data-testid="helm-action-wrong"
            onClick={() => onAction({ itemKey, kind: 'wrong' })}
          >
            That&apos;s wrong
          </DropdownItem>
          {isDeployed && (
            <DropdownItem
              data-testid="helm-action-seen"
              onClick={() => onAction({ itemKey, kind: 'seen' })}
            >
              Seen
            </DropdownItem>
          )}
        </DropdownMenu>
      </Dropdown>
      <DeferDialog
        itemKey={itemKey}
        open={deferOpen}
        onClose={() => setDeferOpen(false)}
        onAction={dispatch}
      />
      <WaitingOnDialog
        itemKey={itemKey}
        open={waitingOpen}
        onClose={() => setWaitingOpen(false)}
        onAction={dispatch}
        recheckRequired={target !== null}
      />
      {target && record && (
        <CloseRosterDialog
          itemKey={itemKey}
          open={doneOpen}
          onClose={() => setDoneOpen(false)}
          onDone={(close) => record.onRecordAction(target, { itemKey, kind: 'done' }, close)}
        />
      )}
    </span>
  );
}
